'use client';

import React, { useState, useEffect, useMemo } from 'react';
import SalesHeader from '@/components/dashboard/sales/SalesHeader';
import SalesKpiStrip from '@/components/dashboard/sales/SalesKpiStrip';
import SalesFilterBar from '@/components/dashboard/sales/SalesFilterBar';
import SalesTable, { SaleRecord } from '@/components/dashboard/sales/SalesTable';
import { ChargeDrawer } from '@/components/dashboard/sales/ChargeDrawer';
import { TodayAppointment } from '@/components/dashboard/sales/ChargeClientModal';
import LooseAppointmentModal from '@/components/dashboard/sales/LooseAppointmentModal';

export default function SalesPage() {
  const [currentDate, setCurrentDate] = useState<string>('2026-10-13');
  const [selectedRange, setSelectedRange] = useState<'day' | 'week' | 'month' | 'all'>('day');
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('ALL');
  const [selectedMethod, setSelectedMethod] = useState('ALL');
  const [selectedSort, setSelectedSort] = useState('RECENT');
  const [selectedFilterState, setSelectedFilterState] = useState('ALL');

  // Datos
  const [sales, setSales] = useState<SaleRecord[]>([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [channelSummary, setChannelSummary] = useState({
    EFECTIVO: 0,
    DEBITO: 0,
    CREDITO: 0,
    TRANSFERENCIA: 0,
    MERCADOPAGO: 0,
    OTRO: 0
  });
  const [todayAppointments, setTodayAppointments] = useState<TodayAppointment[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Modales
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [looseModalOpen, setLooseModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Cargar ventas reales desde PostgreSQL
  const fetchSales = async () => {
    setLoading(true);
    try {
      const url = `/api/sales?date=${currentDate}&range=${selectedRange}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setSales(data.sales || []);
        setGrandTotal(data.grandTotal || 0);
        setChannelSummary(data.channelSummary || {
          EFECTIVO: 0,
          DEBITO: 0,
          CREDITO: 0,
          TRANSFERENCIA: 0,
          MERCADOPAGO: 0,
          OTRO: 0
        });
      }
    } catch (err) {
      console.error('Error fetching sales:', err);
    } finally {
      setLoading(false);
    }
  };

  // Cargar citas de hoy y catálogos auxiliares
  const fetchAuxiliaryData = async () => {
    try {
      const [resAppt, resProd, resSrv, resStaff] = await Promise.all([
        fetch(`/api/bookings?date=${currentDate}`),
        fetch('/api/products'),
        fetch('/api/services'),
        fetch('/api/staff')
      ]);

      const [dataAppt, dataProd, dataSrv, dataStaff] = await Promise.all([
        resAppt.json(),
        resProd.json(),
        resSrv.json(),
        resStaff.json()
      ]);

      if (dataAppt.success) setTodayAppointments(dataAppt.appointments || []);
      if (dataProd.success) setProducts(dataProd.products || []);
      if (dataSrv.success) setServices(dataSrv.services || []);
      if (dataStaff.success) setStaffList(dataStaff.staff || []);
    } catch (err) {
      console.error('Error fetching auxiliary data:', err);
    }
  };

  useEffect(() => {
    fetchSales();
    fetchAuxiliaryData();
  }, [currentDate, selectedRange]);

  // Manejadores de navegación de fechas
  const handlePrevDate = () => {
    const d = new Date(`${currentDate}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  const handleNextDate = () => {
    const d = new Date(`${currentDate}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    setCurrentDate('2026-10-13');
  };

  const isToday = currentDate === '2026-10-13';

  const dateLabel = useMemo(() => {
    const d = new Date(`${currentDate}T12:00:00Z`);
    return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
  }, [currentDate]);

  const modalDateLabel = useMemo(() => {
    const d = new Date(`${currentDate}T12:00:00Z`);
    const dayName = d.toLocaleDateString('es-AR', { weekday: 'long' });
    const dayNumber = d.getUTCDate();
    const monthName = d.toLocaleDateString('es-AR', { month: 'long' });
    return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dayNumber} De ${monthName.charAt(0).toUpperCase() + monthName.slice(1)}`;
  }, [currentDate]);

  // Cobro directo de un turno agendado (con soporte para vouchers y descuentos)
  const handleChargeAppointment = async (
    appt: TodayAppointment,
    channel: string,
    voucherCode?: string,
    discount: number = 0
  ) => {
    try {
      const res = await fetch('/api/sales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId: appt.id,
          staffId: appt.staffId,
          paymentChannel: channel,
          voucherCode,
          discount,
          items: [{
            serviceId: appt.serviceId,
            name: appt.serviceName,
            quantity: 1,
            unitPrice: appt.price,
            staffCommission: 0
          }]
        })
      });
      const data = await res.json();
      if (data.success) {
        const finalCharged = Math.max(0, appt.price - discount);
        showToast(`¡Turno de ${appt.clientName} cobrado con éxito ($${finalCharged.toLocaleString('es-AR')})!`);
        fetchSales();
        fetchAuxiliaryData();
      } else {
        alert('Error cobrando turno: ' + data.error);
      }
    } catch (err: any) {
      alert('Error de conexión: ' + err.message);
    }
  };

  // Venta directa de productos
  const handleChargeProducts = async (payload: any) => {
    try {
      const res = await fetch('/api/sales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentChannel: payload.paymentChannel,
          notes: `Venta mostrador a ${payload.clientName}`,
          items: payload.items
        })
      });
      const data = await res.json();
      if (data.success) {
        showToast('¡Venta de productos registrada y stock actualizado!');
        fetchSales();
        fetchAuxiliaryData();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (err: any) {
      alert('Error: ' + err.message);
    }
  };

  // Alta y cobro simultáneo de Turno Suelto (Sobreturno)
  const handleSubmitLooseAppointment = async (payload: {
    staffId: string;
    time: string;
    serviceId: string;
    clientName: string;
    clientPhone: string;
    paymentChannel: string;
  }) => {
    // 1. Crear el sobreturno en PostgreSQL
    const resAppt = await fetch('/api/bookings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        serviceId: payload.serviceId,
        staffId: payload.staffId,
        date: currentDate,
        time: payload.time,
        clientName: payload.clientName,
        clientPhone: payload.clientPhone,
        isOverturn: true,
        notes: 'Turno suelto registrado y cobrado en mostrador'
      })
    });
    const dataAppt = await resAppt.json();
    if (!dataAppt.success) {
      throw new Error(dataAppt.error || 'No se pudo crear el turno suelto');
    }

    const appt = dataAppt.appointment;

    // 2. Registrar la venta vinculada
    const resSale = await fetch('/api/sales', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appointmentId: appt.id,
        staffId: payload.staffId,
        paymentChannel: payload.paymentChannel,
        items: [{
          serviceId: payload.serviceId,
          name: appt.serviceName,
          quantity: 1,
          unitPrice: appt.price,
          staffCommission: 0
        }]
      })
    });
    const dataSale = await resSale.json();
    if (!dataSale.success) {
      throw new Error(dataSale.error || 'No se pudo registrar el cobro del turno suelto');
    }

    showToast(`¡Turno suelto registrado y cobrado con éxito ($${appt.price.toLocaleString('es-AR')})!`);
    fetchSales();
    fetchAuxiliaryData();
  };

  // Exportar arqueo en CSV
  const handleExportCSV = () => {
    if (sales.length === 0) {
      alert('No hay ventas registradas para exportar en este período.');
      return;
    }
    const headers = ['ID', 'Fecha', 'Hora', 'Cliente', 'Profesional', 'Canal', 'Total', 'Detalle'];
    const rows = sales.map(s => [
      s.id,
      new Date(s.createdAt).toLocaleDateString('es-AR'),
      new Date(s.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      `"${s.client?.name || 'Cliente de Mostrador'}"`,
      `"${s.staff?.name || 'Sin asignar'}"`,
      s.paymentChannel,
      s.total,
      `"${s.items.map(i => `${i.quantity}x ${i.name}`).join(', ')}"`
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `arqueo_ventas_${currentDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtrado y ordenamiento de ventas
  const filteredSales = useMemo(() => {
    return sales
      .filter(s => {
        // Buscador
        if (searchTerm.trim()) {
          const q = searchTerm.toLowerCase();
          const matchClient = s.client?.name?.toLowerCase().includes(q);
          const matchPhone = s.client?.phone?.includes(q);
          const matchItem = s.items.some(it => it.name.toLowerCase().includes(q));
          const matchId = s.id.toLowerCase().includes(q);
          if (!matchClient && !matchPhone && !matchItem && !matchId) return false;
        }

        // Método
        if (selectedMethod !== 'ALL' && s.paymentChannel !== selectedMethod) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (selectedSort === 'RECENT') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        if (selectedSort === 'OLDEST') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        if (selectedSort === 'HIGHEST') return b.total - a.total;
        if (selectedSort === 'LOWEST') return a.total - b.total;
        return 0;
      });
  }, [sales, searchTerm, selectedMethod, selectedSort]);

  return (
    <div className="space-y-4">
      {/* Toast flotante */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 text-sm font-semibold animate-in slide-in-from-bottom-5 duration-200">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
          <span>{toastMessage}</span>
        </div>
      )}

      {/* 1. Cabecera con Título, Conteo, Fecha y Botones de Acción */}
      <SalesHeader
        salesCount={filteredSales.length}
        currentDateStr={currentDate}
        selectedRange={selectedRange}
        onRangeChange={setSelectedRange}
        onPrevDate={handlePrevDate}
        onNextDate={handleNextDate}
        onToday={handleToday}
        isToday={isToday}
        dateLabel={dateLabel}
        onOpenChargeModal={() => setChargeModalOpen(true)}
      />

      {/* 2. Tira de 4 KPIs + Tarjeta Financiera Compuesta de 6 Canales */}
      <SalesKpiStrip
        totalSales={sales.length}
        pendingSales={0}
        deliveredSales={sales.length}
        cancelledSales={0}
        grandTotal={grandTotal}
        channels={channelSummary}
        selectedFilterState={selectedFilterState}
        onSelectFilterState={setSelectedFilterState}
      />

      {/* 3. Barra de Búsqueda, Dropdowns de Pago, Método, Orden y Exportar */}
      <SalesFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedPaymentStatus={selectedPaymentStatus}
        onPaymentStatusChange={setSelectedPaymentStatus}
        selectedMethod={selectedMethod}
        onMethodChange={setSelectedMethod}
        selectedSort={selectedSort}
        onSortChange={setSelectedSort}
        onExport={handleExportCSV}
      />

      {/* 4. Tabla de Pedidos / Transacciones o Empty State Fiel */}
      <SalesTable
        sales={filteredSales}
        loading={loading}
        onOpenChargeModal={() => setChargeModalOpen(true)}
      />

      {/* SLIDE-OVER DRAWER: Cobrar Cliente en Mostrador (480px) */}
      <ChargeDrawer
        isOpen={chargeModalOpen}
        onClose={() => setChargeModalOpen(false)}
        dateLabel={modalDateLabel}
        todayAppointments={todayAppointments}
        onOpenLooseAppointment={() => setLooseModalOpen(true)}
        onChargeAppointment={handleChargeAppointment}
        onChargeProducts={handleChargeProducts}
        products={products}
      />

      {/* MODAL 2: Turno Suelto (Sobreturno inmediato con cobro en el acto) */}
      <LooseAppointmentModal
        isOpen={looseModalOpen}
        onClose={() => setLooseModalOpen(false)}
        staffList={staffList}
        services={services}
        onSubmitLooseAppointment={handleSubmitLooseAppointment}
      />
    </div>
  );
}
